import React from 'react';
import {
    Card,
    Col,
    Progress,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import { FormattedMessage } from 'react-intl';
import { CheckCircleTwoTone, CloseCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
    ICrmActivationAccount,
    ICrmStageFlow,
} from '../../../services/nestjs-base-api/types';
import { useGetCrmReportsQuery } from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';
import { LifecycleTag } from './crmUi';

const { Text, Title } = Typography;

interface CrmReportsTabProps {
    onOpenCompany: (companyId: number) => void;
}

// Activation-first reporting: the conversions that matter are signup → first
// database blast (All Customers cohort) → post-purchase workflow live (Recent
// Purchasers cohort with ongoing starts). Sales analytics follow below.
export const CrmReportsTab: React.FC<CrmReportsTabProps> = ({ onOpenCompany }) => {
    const { user } = useAuth();
    const { data, isLoading } = useGetCrmReportsQuery({ jwtToken: user?.jwtToken || '' });

    if (isLoading || !data) {
        return (
            <div style={{ textAlign: 'center', padding: 60 }}>
                <Spin />
            </div>
        );
    }

    const { activation, sales } = data;
    const pct = (n: number) =>
        activation.funnel.signedUp > 0
            ? Math.round((n / activation.funnel.signedUp) * 100)
            : 0;

    const activationSteps = [
        {
            key: 'signedUp',
            label: (
                <FormattedMessage id="crm.reports.step.signedUp" defaultMessage="Signed up" />
            ),
            count: activation.funnel.signedUp,
        },
        {
            key: 'firstBlast',
            label: (
                <FormattedMessage
                    id="crm.reports.step.firstBlast"
                    defaultMessage="First send to database (All Customers)"
                />
            ),
            count: activation.funnel.firstBlast,
        },
        {
            key: 'workflowStarted',
            label: (
                <FormattedMessage
                    id="crm.reports.step.workflowStarted"
                    defaultMessage="Post-purchase workflow started (Recent Purchasers)"
                />
            ),
            count: activation.funnel.workflowStarted,
        },
        {
            key: 'workflowActive',
            label: (
                <FormattedMessage
                    id="crm.reports.step.workflowActive"
                    defaultMessage="Workflow active (starts in last 14 days)"
                />
            ),
            count: activation.funnel.workflowActive,
        },
    ];

    const dateOrDash = (d: string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '—');

    const activationColumns: ColumnsType<ICrmActivationAccount> = [
        {
            title: <FormattedMessage id="crm.column.name" defaultMessage="Account" />,
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            render: (name, a) => <a onClick={() => onOpenCompany(a.crmCompanyId)}>{name}</a>,
        },
        {
            title: <FormattedMessage id="crm.column.lifecycle" defaultMessage="Lifecycle" />,
            key: 'lifecycle',
            render: (_, a) => <LifecycleTag stage={a.lifecycleStage} />,
        },
        {
            title: <FormattedMessage id="crm.reports.signedUp" defaultMessage="Signed up" />,
            key: 'signedUp',
            sorter: (a, b) =>
                new Date(a.signedUpAt).getTime() - new Date(b.signedUpAt).getTime(),
            render: (_, a) => dateOrDash(a.signedUpAt),
        },
        {
            title: (
                <FormattedMessage id="crm.reports.firstBlast" defaultMessage="First blast" />
            ),
            key: 'firstBlast',
            sorter: (a, b) => (a.daysToFirstBlast ?? 9999) - (b.daysToFirstBlast ?? 9999),
            render: (_, a) =>
                a.firstBlastAt ? (
                    <Tooltip title={dateOrDash(a.firstBlastAt)}>
                        <Tag color="green">
                            <FormattedMessage
                                id="crm.reports.daysAfterSignup"
                                defaultMessage="+{days}d"
                                values={{ days: a.daysToFirstBlast }}
                            />
                        </Tag>
                    </Tooltip>
                ) : (
                    <Tag>
                        <FormattedMessage id="crm.reports.never" defaultMessage="Never" />
                    </Tag>
                ),
        },
        {
            title: <FormattedMessage id="crm.reports.workflow" defaultMessage="Workflow" />,
            key: 'workflow',
            sorter: (a, b) => Number(a.workflowActive) - Number(b.workflowActive),
            render: (_, a) =>
                a.workflowActive ? (
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="crm.reports.workflowLastStart"
                                defaultMessage="Last start: {date}"
                                values={{ date: dateOrDash(a.lastWorkflowStartAt) }}
                            />
                        }
                    >
                        <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>
                            {' '}
                            <FormattedMessage id="crm.reports.active" defaultMessage="Active" />
                        </Tag>
                    </Tooltip>
                ) : a.firstWorkflowStartAt ? (
                    <Tooltip
                        title={
                            <FormattedMessage
                                id="crm.reports.workflowLastStart"
                                defaultMessage="Last start: {date}"
                                values={{ date: dateOrDash(a.lastWorkflowStartAt) }}
                            />
                        }
                    >
                        <Tag color="orange">
                            <FormattedMessage id="crm.reports.stalled" defaultMessage="Stalled" />
                        </Tag>
                    </Tooltip>
                ) : (
                    <Tag icon={<CloseCircleOutlined />}>
                        {' '}
                        <FormattedMessage id="crm.reports.notSetUp" defaultMessage="Not set up" />
                    </Tag>
                ),
        },
        {
            title: (
                <FormattedMessage id="crm.reports.starts30" defaultMessage="Starts (30d)" />
            ),
            key: 'starts',
            sorter: (a, b) =>
                a.blastStarts30d + a.workflowStarts30d - (b.blastStarts30d + b.workflowStarts30d),
            render: (_, a) => a.blastStarts30d + a.workflowStarts30d || '—',
        },
        {
            title: <FormattedMessage id="crm.usage.responses" defaultMessage="Total responses" />,
            dataIndex: 'responseCount',
            key: 'responses',
            sorter: (a, b) => a.responseCount - b.responseCount,
            render: (v) => v || '—',
        },
    ];

    const stageFlowColumns: ColumnsType<ICrmStageFlow> = [
        {
            title: <FormattedMessage id="crm.column.stage" defaultMessage="Stage" />,
            key: 'stage',
            render: (_, s) => <Tag color={s.color}>{s.label}</Tag>,
        },
        {
            title: <FormattedMessage id="crm.reports.entered" defaultMessage="Entered" />,
            dataIndex: 'entered',
            key: 'entered',
        },
        {
            title: <FormattedMessage id="crm.reports.inStageNow" defaultMessage="In stage now" />,
            dataIndex: 'currentlyIn',
            key: 'currentlyIn',
        },
        {
            title: <FormattedMessage id="crm.reports.conversion" defaultMessage="Moved forward" />,
            key: 'conversion',
            render: (_, s) =>
                s.conversionRate == null ? (
                    '—'
                ) : (
                    <Progress
                        percent={s.conversionRate}
                        size="small"
                        style={{ width: 120 }}
                        format={(p) => `${p}%`}
                    />
                ),
        },
        {
            title: <FormattedMessage id="crm.reports.avgDays" defaultMessage="Avg days" />,
            dataIndex: 'avgDaysInStage',
            key: 'avgDays',
            render: (v) => v ?? '—',
        },
        {
            title: <FormattedMessage id="crm.reports.medianDays" defaultMessage="Median days" />,
            dataIndex: 'medianDaysInStage',
            key: 'medianDays',
            render: (v) => v ?? '—',
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
                size="small"
                title={
                    <FormattedMessage
                        id="crm.reports.activationTitle"
                        defaultMessage="Activation — signup to live data collection"
                    />
                }
            >
                <Row gutter={[24, 16]}>
                    <Col xs={24} md={14}>
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            {activationSteps.map((step) => (
                                <div key={step.key}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: 2,
                                        }}
                                    >
                                        <Text>{step.label}</Text>
                                        <Text strong>
                                            {step.count}
                                            <Text type="secondary"> · {pct(step.count)}%</Text>
                                        </Text>
                                    </div>
                                    <Progress
                                        percent={pct(step.count)}
                                        showInfo={false}
                                        strokeColor="#18216d"
                                    />
                                </div>
                            ))}
                        </Space>
                    </Col>
                    <Col xs={24} md={10}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Statistic
                                    title={
                                        <FormattedMessage
                                            id="crm.reports.avgDaysToBlast"
                                            defaultMessage="Avg days to first blast"
                                        />
                                    }
                                    value={activation.avgDaysToFirstBlast ?? '—'}
                                />
                            </Col>
                            <Col span={12}>
                                <Statistic
                                    title={
                                        <FormattedMessage
                                            id="crm.reports.medianDaysToBlast"
                                            defaultMessage="Median days to first blast"
                                        />
                                    }
                                    value={activation.medianDaysToFirstBlast ?? '—'}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Card>

            <Table
                rowKey="crmCompanyId"
                size="small"
                dataSource={activation.accounts}
                columns={activationColumns}
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 25 }}
            />

            <Title level={5} style={{ margin: '8px 0 0' }}>
                <FormattedMessage id="crm.reports.salesTitle" defaultMessage="Sales pipeline" />
            </Title>
            <Row gutter={[16, 16]}>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={<FormattedMessage id="crm.reports.won" defaultMessage="Won" />}
                            value={sales.won}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={<FormattedMessage id="crm.reports.lost" defaultMessage="Lost" />}
                            value={sales.lost}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={
                                <FormattedMessage
                                    id="crm.reports.avgDaysToWin"
                                    defaultMessage="Avg days to win"
                                />
                            }
                            value={sales.avgDaysToWin ?? '—'}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={
                                <FormattedMessage
                                    id="crm.reports.mrrWon"
                                    defaultMessage="MRR won (monthly)"
                                />
                            }
                            value={sales.mrrWonMonthly}
                            prefix="$"
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card
                        size="small"
                        title={
                            <FormattedMessage
                                id="crm.reports.stageFlowTitle"
                                defaultMessage="Stage flow & dwell time"
                            />
                        }
                    >
                        <Table
                            rowKey="code"
                            size="small"
                            dataSource={sales.stageFlow}
                            columns={stageFlowColumns}
                            pagination={false}
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card
                        size="small"
                        title={
                            <FormattedMessage
                                id="crm.reports.lostReasonsTitle"
                                defaultMessage="Lost reasons"
                            />
                        }
                        style={{ marginBottom: 16 }}
                    >
                        {sales.lostReasons.length === 0 ? (
                            <Text type="secondary">—</Text>
                        ) : (
                            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                {sales.lostReasons.map((r) => (
                                    <div
                                        key={r.reason}
                                        style={{ display: 'flex', justifyContent: 'space-between' }}
                                    >
                                        <Text>
                                            <FormattedMessage
                                                id={`crm.lostReason.${r.reason}`}
                                                defaultMessage={r.reason}
                                            />
                                        </Text>
                                        <Text strong>{r.count}</Text>
                                    </div>
                                ))}
                            </Space>
                        )}
                    </Card>
                    <Card
                        size="small"
                        title={
                            <FormattedMessage
                                id="crm.reports.mrrBySourceTitle"
                                defaultMessage="MRR by source"
                            />
                        }
                    >
                        {sales.mrrBySource.length === 0 ? (
                            <Text type="secondary">—</Text>
                        ) : (
                            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                {sales.mrrBySource.map((r) => (
                                    <div
                                        key={r.source}
                                        style={{ display: 'flex', justifyContent: 'space-between' }}
                                    >
                                        <Text>
                                            <FormattedMessage
                                                id={`crm.source.${r.source}`}
                                                defaultMessage={r.source}
                                            />
                                            <Text type="secondary"> · {r.accounts}</Text>
                                        </Text>
                                        <Text strong>${r.mrrMonthly}/mo</Text>
                                    </div>
                                ))}
                            </Space>
                        )}
                    </Card>
                </Col>
            </Row>
        </Space>
    );
};
